import { useState } from 'react';
import api from '@/lib/api';
import { usePwaInstallPrompt } from '@/hooks/usePwaInstallPrompt';
import {
  HeartPulse, BellRing, ShieldCheck, Smartphone, Apple, Share2,
  CheckCircle2, ArrowRight, Loader2, X,
} from 'lucide-react';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * InstallNotifyGate
 * ─────────────────
 * Shown ONCE after the patient enters their mobile on the QR check-in
 * page (before the rest of the form opens). Sells one outcome in one
 * sentence — "Your health symptom will be automatically tracked till
 * healed" — and asks for the two free permissions that make day-7
 * outcome tracking possible without any SMS cost:
 *
 *   1. Install DocSite to the patient's home screen (PWA)
 *   2. Allow browser notifications (Web Push)
 *
 * Subscribes the patient's push endpoint against the doctor's slug with a
 * phone-derived token so the backend cron can fire the Q4 reminder on
 * day 7 without ever needing a server-stored patient identity.
 *
 * Hard-fails gracefully: if the browser refuses (iOS pre-16.4, denied
 * permission, no SW), the patient is simply ushered forward — no
 * dead-ends. We never block the medical workflow.
 */
export default function InstallNotifyGate({ phone, doctorSlug, doctorName, onContinue }) {
  const { canInstall, promptInstall, isIOS, isStandalone } = usePwaInstallPrompt();
  const [status, setStatus] = useState('idle'); // idle | working | done | iosManual
  const [stepDone, setStepDone] = useState({ install: false, notify: false });
  const [pushSupported] = useState(() =>
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY,
  );

  const subscribePush = async () => {
    if (!pushSupported) return false;
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }
      // Anonymous patient — backend hashes raw phone with same hash_field()
      // used for the visit's patient_phone_hash, so the day-7 cron can
      // resolve this subscription via send_push_to_phone_token(phone_hash).
      await api.post('/public/chat-push/subscribe', {
        phone,
        doctor_slug: doctorSlug,
        subscription: sub.toJSON(),
      });
      return true;
    } catch (e) {
      console.warn('[InstallNotifyGate] subscribe failed', e);
      return false;
    }
  };

  const handleEnable = async () => {
    setStatus('working');

    // Step 1: install. iOS has no programmatic install — surface the
    // Share→Add to Home Screen panel and STOP here. The patient has to
    // leave Safari, install, reopen from the home-screen icon, then tap
    // again. Android/Chrome fires the native install sheet via the
    // deferred prompt we captured.
    if (!isStandalone) {
      if (isIOS) {
        // iOS path — render manual instructions and wait. Do NOT auto-
        // continue: subscribePush() would fire Notification.requestPermission()
        // which on iOS Safari (non-standalone) silently no-ops and burns the
        // permission state. The patient must reopen from the home screen
        // first; on that fresh load the gate hash already lives in
        // sessionStorage so they skip past it cleanly.
        setStatus('iosManual');
        return;
      }
      if (canInstall) {
        try {
          await promptInstall();
        } catch { /* user dismissed */ }
      }
    }
    const installed = isStandalone || true; // best-effort
    setStepDone(s => ({ ...s, install: installed }));

    // Step 2: notification permission + push subscribe (Android / installed PWA).
    const subscribed = await subscribePush();
    setStepDone(s => ({ ...s, notify: subscribed }));

    setStatus('done');
    // Brief celebration, then continue.
    setTimeout(() => { onContinue?.({ installed, subscribed }); }, 1100);
  };

  const handleSkip = () => onContinue?.({ skipped: true });
  // After the iOS patient has (presumably) added DocSite to their home
  // screen, they tap "I've done it" to dismiss the gate and proceed with
  // their check-in. The actual push subscribe will happen the next time
  // they open the PWA from the home screen (sessionStorage is intentionally
  // session-scoped so a fresh home-screen launch can re-prompt cleanly).
  const handleIosDone = () => onContinue?.({ iosManual: true });

  return (
    <div className="space-y-4" data-testid="install-notify-gate">
      <div className="rounded-2xl border-2 border-sky-200 bg-gradient-to-br from-sky-50 via-emerald-50/70 to-white p-5 shadow-sm">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-white border border-sky-200 flex items-center justify-center shrink-0 shadow-sm">
            <HeartPulse size={24} className="text-rose-500" strokeWidth={2.3} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-700">One quick set-up · 10 seconds</p>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 leading-snug mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="install-notify-headline">
              DocSite helps you stay connected with {doctorName || 'this doctor'} for notifications on this and other associated symptoms.
            </h2>
            <p className="text-[11px] text-emerald-700 font-semibold mt-1.5">₹0 cost to you · critical for your healing success</p>
          </div>
        </div>

        {/* What you get — 3 lines, one per benefit */}
        <ul className="mt-4 space-y-1.5">
          {[
            { Icon: BellRing, text: <>One free in-app reminder on day 7 — <b>"Did the treatment work?"</b></> },
            { Icon: ShieldCheck, text: <>Your phone number stays private — never sold, never shared</> },
            { Icon: Smartphone, text: <>App lives on your home screen — no Play Store download</> },
          ].map(({ Icon, text }, i) => (
            <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700">
              <Icon size={14} className="text-emerald-600 mt-0.5 shrink-0" />
              <span>{text}</span>
            </li>
          ))}
        </ul>

        {/* iOS-only manual panel — Share → Add to Home Screen → reopen.
            Android users (`isIOS=false`) never see this card. */}
        {isIOS && status === 'iosManual' && (
          <div className="mt-4 rounded-xl bg-white border border-amber-200 p-3.5" data-testid="install-notify-ios-steps">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1.5"><Apple size={12} /> iPhone — 3 quick steps</p>
            <ol className="text-xs text-slate-700 mt-2 space-y-1.5 list-decimal pl-4">
              <li>Tap the <Share2 size={12} className="inline -mt-0.5 mx-0.5" /> <b>Share</b> button at the bottom of Safari</li>
              <li>Scroll down → tap <b>"Add to Home Screen"</b> → tap <b>Add</b></li>
              <li>Close Safari → open the new <b>DocSite</b> icon on your home screen → tap <b>Quick Check In</b> again → tap <b>Allow</b> when iPhone asks about notifications</li>
            </ol>
            <p className="text-[10px] text-amber-700 mt-2 font-semibold">iPhones need this one-time setup. Android phones do it in one tap automatically.</p>
            <div className="mt-3 flex flex-col gap-1.5">
              <button
                type="button"
                onClick={handleIosDone}
                className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
                data-testid="install-notify-ios-done"
              >
                I've added DocSite — continue check-in
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="text-[10px] text-slate-500 hover:text-slate-800 underline self-center"
                data-testid="install-notify-ios-skip"
              >
                Skip — I'll set up notifications later
              </button>
            </div>
          </div>
        )}

        {/* Status row after attempt */}
        {status === 'done' && (
          <div className="mt-4 grid grid-cols-2 gap-2" data-testid="install-notify-result">
            <StatusPill ok={stepDone.install} label={stepDone.install ? 'Installed' : 'Skipped'} />
            <StatusPill ok={stepDone.notify} label={stepDone.notify ? 'Alerts on' : 'Alerts off'} />
          </div>
        )}

        {/* Primary + skip — hidden during iosManual (iOS card has its own
            continue/skip buttons) and once we're showing the done pills. */}
        {status !== 'done' && status !== 'iosManual' && (
          <div className="mt-5 flex flex-col gap-2">
            <button
              type="button"
              onClick={handleEnable}
              disabled={status === 'working'}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-60 shadow-sm"
              data-testid="install-notify-enable"
            >
              {status === 'working' ? <Loader2 size={15} className="animate-spin" /> : <BellRing size={15} />}
              {status === 'working' ? 'Setting up…' : 'QUICK CHECK IN'}
            </button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline self-center"
              data-testid="install-notify-skip"
            >
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[11px] font-semibold ${ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
      {ok ? <CheckCircle2 size={13} /> : <X size={13} />}
      {label}
    </div>
  );
}
